import type { StageProgress } from '../types/run.api.types';

import { parseSourceProductId } from './source-product-id';

/** Placeholder for an absent value — never a zero, which would claim a measurement. */
export const DASH = '—';

/** Locale count, or a dash when the count was not reported. */
export function formatCount(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString() : DASH;
}

/**
 * A score for display, at most `digits` decimals with a trailing ".0" dropped ("95.7" stays,
 * "95.69999694824219" → "95.7", "95.0" → "95") — the single place every score on screen is
 * rounded, so a float never leaks straight from the wire into the UI. Catalog decimals arrive as
 * strings and content-gen's as numbers; both are only formatted here, never computed on.
 */
export function formatScore(value: number | string | null | undefined, digits = 1): string {
  if (value === null || value === undefined || value === '') return DASH;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  const fixed = n.toFixed(digits);
  return digits > 0 ? fixed.replace(/\.0+$/, '') : fixed;
}

/** A lift / delta with an explicit sign (`+1.9`, `-0.4`). */
export function formatLift(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return `${value > 0 ? '+' : ''}${value.toFixed(digits)}`;
}

/** The shared "pre → post lift" trio, at one fixed precision. */
export interface ScoreTrio {
  pre: string;
  post: string;
  lift: string;
  /** The rounded numeric lift (post − pre, both rounded to `digits` first) — for sign/tone
   *  checks without re-parsing `lift`; `null` when either side is missing. */
  liftValue: number | null;
  /** The rounded numeric endpoints, for a bar that draws them — `null` when missing. */
  preValue: number | null;
  postValue: number | null;
}

/**
 * R3-1 — the ONE place a "before → after lift" trio is formatted: `pre`/`post` are rounded to the
 * SAME fixed precision and shown at that precision (never one side trimmed to fewer decimals than
 * the other, the way `formatScore` alone does), and `lift` is derived from those same ROUNDED
 * values rather than the raw inputs — so the lift shown always equals the subtraction a reader can
 * do from the pre/post numbers on screen ("90 → 93.6 +3.5" disagreeing is exactly the bug this
 * fixes).
 */
export function formatScoreTrio(
  pre: number | string | null | undefined,
  post: number | string | null | undefined,
  digits = 1,
): ScoreTrio {
  const toNumber = (value: number | string | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
  };
  const factor = 10 ** digits;
  const round = (n: number): number => Math.round(n * factor) / factor;
  const preNumber = toNumber(pre);
  const postNumber = toNumber(post);
  const preRounded = preNumber === null ? null : round(preNumber);
  const postRounded = postNumber === null ? null : round(postNumber);
  const liftValue = preRounded !== null && postRounded !== null ? postRounded - preRounded : null;
  return {
    pre: preRounded === null ? DASH : preRounded.toFixed(digits),
    post: postRounded === null ? DASH : postRounded.toFixed(digits),
    lift: formatLift(liftValue, digits),
    liftValue,
    preValue: preRounded,
    postValue: postRounded,
  };
}

/** `UPPER_SNAKE` → `Upper snake`; the fallback label for a stage / status the locale does not name. */
export function humanizeCode(code: string): string {
  const words = code.toLowerCase().split('_').join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A dotted field/criterion path (`description.readability`) → "Description · Readability" — the
 *  score summary's "weakest fields" list showed the raw dotted code verbatim (a real bug: a dot
 *  isn't a `_`, so `humanizeCode` alone left it untouched); each segment is humanised on its own
 *  and joined with the same "·" separator the rest of this screen uses for a compound label. */
export function humanizeFieldPath(path: string): string {
  return path
    .split('.')
    .filter(Boolean)
    .map((segment) => humanizeCode(segment))
    .join(' · ');
}

/** One of a stage's snake_case counters (`products_succeeded`, …); undefined when not reported. */
export function stageCount(stage: StageProgress | undefined, key: string): number | undefined {
  return stage?.counts?.[key];
}

/** Finds one stage's newest attempt in a run's progress, by code — shared by the batch Products
 *  table and the "In this batch" strip (S8) so both read the same stage the same way. */
export function findStage(
  stages: StageProgress[] | undefined,
  code: string,
): StageProgress | undefined {
  return stages?.find((s) => s.stage === code);
}

/** Semantic tone of a run / stage / delivery status, for chips and rail cells. */
export type StatusTone = 'success' | 'running' | 'waiting' | 'failed' | 'neutral';

/**
 * Maps every status vocabulary the run screens show (§0.8) onto one tone — run /
 * stage statuses, PRE/POST tier verdicts (PASS/REVIEW/FAIL) and delivery sync
 * outcomes (SYNCED/SYNC_FAILED/VALIDATION_FAILED), so every semantic chip (Q19)
 * uses the same success/waiting/failed palette regardless of which vocabulary a
 * code comes from.
 */
export function statusTone(status: string | null | undefined): StatusTone {
  switch (status) {
    case 'COMPLETED':
    case 'DELIVERED':
    case 'SUCCESS':
    case 'PASS':
    case 'SYNCED':
      return 'success';
    case 'RUNNING':
    case 'DELIVERING':
      return 'running';
    case 'AWAITING_GATE':
    case 'UNCONFIRMED':
    case 'PAUSED':
    case 'REVIEW':
    case 'EXCLUDED':
      return 'waiting';
    case 'FAILED':
    case 'RECONCILE_FAILED':
    case 'REJECTED':
    case 'TERMINATED':
    case 'FAIL':
    case 'SYNC_FAILED':
    case 'VALIDATION_FAILED':
      return 'failed';
    default:
      return 'neutral';
  }
}

/**
 * Q18 — a one-line summary of a product's connector ids for layouts that can't render
 * `ProductCell`'s market chips (e.g. a plain card subtitle): "offer id · market1, market2"
 * instead of the raw 50-character connector path.
 */
export function sourceIdSummary(ids: (string | null | undefined)[]): string {
  const parsed = ids.map((id) => parseSourceProductId(id)).filter((p) => p !== null);
  if (parsed.length === 0) return DASH;
  const offerIds = [...new Set(parsed.map((p) => p.offerId))];
  const markets = [...new Set(parsed.map((p) => p.country).filter((c): c is string => !!c))];
  return `${offerIds.join(', ')}${markets.length > 0 ? ` · ${markets.join(', ')}` : ''}`;
}

/** Token classes per tone (design tokens only). Success reads in plain ink: colour is only for
 *  problems and live work (guide §4.7 — no green). */
export const TONE_CLASSES: Record<StatusTone, string> = {
  success: 'bg-background text-foreground border-border',
  running: 'bg-info-50 text-info border-info/30',
  waiting: 'bg-warning-50 text-warning border-warning/30',
  failed: 'bg-error-50 text-error border-error/30',
  neutral: 'bg-muted text-muted-foreground border-border',
};

/**
 * R7 — a list inside a card that grows with data scrolls inside the card instead of
 * stretching the page; pair with `STICKY_HEAD_CLASS` on its header row.
 */
export const BOUNDED_LIST_CLASS = 'max-h-[min(420px,60vh)] overflow-y-auto';

/** R7 — keeps a bounded list's table header in view while its rows scroll. */
export const STICKY_HEAD_CLASS = 'bg-card sticky top-0 z-10';

/** `part / total` as a CSS width ("42%") for drawn split / progress bars; 0% when total is 0. */
export function percentWidth(part: number, total: number): string {
  const pct = total > 0 ? Math.max(0, Math.min(100, (part / total) * 100)) : 0;
  return `${String(pct)}%`;
}
